import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { userById } from '../data';
import { User } from '../types';
import { useProfiles } from '../utils/ProfilesContext';
import { getFirstName } from '../utils/tripUtils';
import { getAvatarColor } from '../utils/avatarColors';

interface UserAvatarProps {
  key?: React.Key;
  userId?: string;
  user?: User;
  size?: number;
  ring?: boolean;
  /** Force two initials regardless of avatar size. */
  twoInitials?: boolean;
  /** Suppress the name tooltip on hover. */
  noTooltip?: boolean;
  /** Opt in where an avatar is the only visible name affordance. */
  tapToReveal?: boolean;
}

export function UserAvatar({ userId, user, size = 24, ring = false, twoInitials = false, noTooltip = false, tapToReveal = false }: UserAvatarProps) {
  const { getProfile, subscribeToIds } = useProfiles();
  const effectiveUserId = userId || user?.id;
  
  useEffect(() => {
    if (effectiveUserId) {
      subscribeToIds([effectiveUserId]);
    }
  }, [effectiveUserId, subscribeToIds]);

  const liveUser = effectiveUserId ? getProfile(effectiveUserId) : null;
  const u = liveUser || user || (userId ? userById(userId) : null);
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!u) return null;

  // Resolved per field, not per object. The live profile wins the whole
  // record, so a users doc with no photo used to shadow a picture the caller
  // already had in hand — a traveller record on the trip, say — and the
  // avatar dropped to initials for someone who visibly has one elsewhere.
  // Whoever has a picture provides it.
  const image = u.imgSrc || u.photoURL || user?.imgSrc || user?.photoURL || null;
  const hasImage = !!image;
  // Palette colour — only used when there is no profile photo
  const paletteColor = getAvatarColor(u.id);
  const ringWidth = ring ? 2 : 0;
  const ringColor = ring ? (hasImage ? 'rgba(0,0,0,0.12)' : paletteColor.text + '55') : 'transparent';

  // Show only the first initial at small sizes unless twoInitials is forced
  const displayInitials = (twoInitials || size >= 26) ? (u.initials ?? '') : (u.initials?.[0] ?? '');

  return (
    <div className="relative inline-flex items-center">
      <div
        role={tapToReveal ? 'button' : undefined}
        tabIndex={tapToReveal ? 0 : undefined}
        aria-label={tapToReveal ? `Show name: ${u.name}` : undefined}
        aria-expanded={tapToReveal ? showTooltip : undefined}
        onClick={tapToReveal ? () => setShowTooltip(value => !value) : undefined}
        onBlur={tapToReveal ? () => setShowTooltip(false) : undefined}
        onKeyDown={tapToReveal ? event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setShowTooltip(value => !value); }
          if (event.key === 'Escape') setShowTooltip(false);
        } : undefined}
        onMouseEnter={() => !noTooltip && !tapToReveal && setShowTooltip(true)}
        onMouseLeave={() => !tapToReveal && setShowTooltip(false)}
        className="rounded-full flex-shrink-0 flex items-center justify-center transition duration-300 group cursor-help relative"
        style={{
          width: size,
          height: size,
          padding: 0,
          border: `${ringWidth}px solid ${ringColor}`,
        }}
      >
        {/* Avatar Image/Initials Wrapper */}
        <div
          className="w-full h-full rounded-full overflow-hidden flex items-center justify-center shadow-sm relative z-10"
          style={{
            backgroundColor: hasImage ? 'transparent' : paletteColor.bg,
          }}
        >
          {hasImage ? (
            <img src={image} alt={u.name} className="w-full h-full object-cover" />
          ) : (
            <span style={{ color: paletteColor.text, fontSize: size * 0.4 }} className="font-bold uppercase font-display leading-none">
              {displayInitials}
            </span>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showTooltip && (
          <motion.div 
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[60] pointer-events-none"
          >
            <div className="bg-app-bg border border-app-border-mid px-2.5 py-1 rounded-lg shadow-xl shadow-black/10 flex flex-col items-center">
              <div className="text-[9px] font-black uppercase tracking-widest text-app-text whitespace-nowrap">
                {tapToReveal ? u.name : getFirstName(u.name)}
              </div>
              {/* Arrow */}
              <div className="w-1.5 h-1.5 bg-app-bg border-r border-b border-app-border-mid rotate-45 -mb-[4px] -mt-[1px]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * AvatarGroup — shows a row of user avatars.
 *
 * Overflow rules (max defaults to 6):
 *   ≤ max users  → all shown individually, no chip
 *   > max users  → show (max − 1) avatars + "+N" chip  (chip always ≥ +2)
 *
 * Examples with max=6:
 *   6 users  → 6 avatars, no chip
 *   7 users  → 5 avatars + "+2"
 *   8 users  → 5 avatars + "+3"
 *
 * The popover renders via a portal so it is never clipped by overflow:hidden
 * parents. A ref on the popover itself prevents outside-click from firing
 * when the user interacts with the popover content.
 */
export function AvatarGroup({
  users,
  size = 24,
  max = 6,
  ring = false,
  twoInitials = false,
  tapToReveal = false,
}: {
  users: User[];
  size?: number;
  max?: number;
  ring?: boolean;
  twoInitials?: boolean;
  tapToReveal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRef      = useRef<HTMLButtonElement>(null);
  const popoverRef   = useRef<HTMLDivElement>(null);

  // Overflow: show all when ≤ max; when > max, show (max-1) + chip so chip ≥ +2
  const needsChip    = users.length > max;
  const visible      = needsChip ? users.slice(0, max - 1) : users;
  const extra        = needsChip ? users.length - (max - 1) : 0;

  // Calculate fixed position — open above chip if near screen bottom
  const openPopover = useCallback(() => {
    if (!chipRef.current) return;
    const r   = chipRef.current.getBoundingClientRect();
    const vh  = window.innerHeight;
    const POPOVER_H = 40 + users.length * 32; // rough estimate
    const spaceBelow = vh - r.bottom;
    if (spaceBelow >= POPOVER_H || spaceBelow > vh / 2) {
      // open downward
      setPopoverStyle({ top: r.bottom + 8, left: r.left });
    } else {
      // open upward
      setPopoverStyle({ bottom: vh - r.top + 8, left: r.left });
    }
    setOpen(v => !v);
  }, [users.length]);

  // Close on outside click — must NOT close when clicking inside the portal popover
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insidePopover   = popoverRef.current?.contains(target);
      if (!insideContainer && !insidePopover) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5">
      {visible.map(u => (
        <UserAvatar key={u.id} user={u} size={size} ring={ring} twoInitials={twoInitials} tapToReveal={tapToReveal} />
      ))}

      {needsChip && (
        <button
          ref={chipRef}
          onClick={openPopover}
          style={{ width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.35)) }}
          className="rounded-full bg-app-faint border border-app-border flex items-center justify-center font-black text-app-sub hover:bg-gold/10 hover:text-gold transition-colors flex-shrink-0"
        >
          +{extra}
        </button>
      )}

      {/* Portal — renders at document.body, escaping all overflow:hidden parents */}
      {open && createPortal(
        <AnimatePresence>
          <motion.div
            ref={popoverRef}
            key="avatar-group-popover"
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.12 }}
            style={{ position: 'fixed', zIndex: 300, ...popoverStyle }}
            className="bg-white border border-app-border rounded-2xl shadow-xl p-3 min-w-[180px]"
          >
            <p className="text-[8px] font-black uppercase tracking-widest text-app-sub/50 mb-2.5">
              All travellers
            </p>
            <div className="flex flex-col items-start gap-2">
              {users.map(u => <TravellerPill key={u.id} user={u} size={32} />)}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

export function By({ userId, user, className = "" }: { userId?: string; user?: User; className?: string }) {
  const { getProfile, subscribeToIds } = useProfiles();
  const effectiveUserId = userId || user?.id;

  useEffect(() => {
    if (effectiveUserId) {
      subscribeToIds([effectiveUserId]);
    }
  }, [effectiveUserId, subscribeToIds]);

  const liveUser = effectiveUserId ? getProfile(effectiveUserId) : null;
  const u = liveUser || user || (userId ? userById(userId) : null);

  if (!u) return null;
  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <UserAvatar user={u} size={16} />
      <span className="text-[10px] font-bold tracking-tight uppercase text-app-sub/60">
        {getFirstName(u.name)}
      </span>
    </div>
  );
}

/**
 * ByInline — inline variant of By for embedding mid-sentence in notification text.
 * Renders as <span> so it sits in text flow alongside formatMessage output.
 */
export function ByInline({ userId, user }: { userId?: string; user?: User }) {
  const { getProfile, subscribeToIds } = useProfiles();
  const effectiveUserId = userId || user?.id;

  useEffect(() => {
    if (effectiveUserId) subscribeToIds([effectiveUserId]);
  }, [effectiveUserId, subscribeToIds]);

  const liveUser = effectiveUserId ? getProfile(effectiveUserId) : null;
  const u = liveUser || user || (userId ? userById(userId) : null);

  if (!u) return null;
  return (
    <span className="inline-flex items-center gap-0.5 align-middle mr-1">
      <UserAvatar user={u} size={13} />
      <span className="font-semibold text-[10px] text-app-sub">
        {getFirstName(u.name)}
      </span>
    </span>
  );
}

/**
 * One traveller as photo + first name — the layout every traveller picker and
 * name list uses. Selected: gold outline; not selected: photo and name greyed.
 * Without onClick it's a plain label (e.g. the travellers dropdown).
 */
export function TravellerPill({ user, selected = true, onClick, locked = false, size = 40 }: {
  user: User; selected?: boolean; onClick?: () => void; locked?: boolean; size?: number;
}) {
  const body = <>
    <span className={`rounded-full transition duration-200 ${selected ? '' : 'grayscale opacity-40'}`}><UserAvatar userId={user.id} user={user} size={size} noTooltip /></span>
    <span className={`transition-colors ${selected ? 'text-app-text' : 'text-app-sub/60'}`}>{getFirstName(user.name)}</span>
  </>;
  const look = `flex items-center gap-2 min-h-[52px] rounded-[28px] border py-[5px] pl-[5px] pr-3 text-[13px] font-semibold transition-colors ${selected ? 'border-gold bg-gold-soft' : 'border-app-border'}`;
  return onClick
    ? <button type="button" aria-pressed={selected} aria-disabled={locked || undefined} onClick={locked ? undefined : onClick} className={`${look} ${locked ? 'cursor-default' : ''}`}>{body}</button>
    : <span className={look}>{body}</span>;
}
